import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaArrowLeft, FaCheck, FaDotCircle, FaCheckSquare, FaAlignLeft } from 'react-icons/fa';
import { Modal, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const SurveyQuestions = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'radio',
    options: ['', ''],
    is_required: false
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchSurvey();
    fetchQuestions();
  }, [id]);

  const fetchSurvey = async () => {
    try {
      const response = await api.surveys.getById(id);
      setSurvey(response.data.data);
    } catch (error) {
      toast.error('Failed to load survey');
      navigate('/admin/surveys');
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await api.surveys.getQuestions(id);
      const questionsData = response.data.data || [];

      const parsedQuestions = questionsData.map(question => ({
        ...question,
        options: typeof question.options === 'string' 
          ? JSON.parse(question.options) 
          : Array.isArray(question.options) 
            ? question.options 
            : []
      }));
      
      setQuestions(parsedQuestions);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to load questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      const questionOptions = Array.isArray(question.options) ? question.options : ['', ''];
      setFormData({
        question_text: question.question_text,
        question_type: question.question_type,
        options: questionOptions.length > 0 ? questionOptions : ['', ''],
        is_required: question.is_required || false
      });
    } else {
      setEditingQuestion(null);
      setFormData({
        question_text: '',
        question_type: 'radio',
        options: ['', ''],
        is_required: false
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingQuestion(null);
    setFormData({
      question_text: '',
      question_type: 'radio',
      options: ['', ''],
      is_required: false
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, options: newOptions }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.question_text.trim()) {
      errors.question_text = 'Question text is required';
    }
    
    if ((formData.question_type === 'radio' || formData.question_type === 'checkbox') && 
        formData.options.filter(opt => opt.trim()).length < 2) {
      errors.options = 'At least 2 options are required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setModalLoading(true);

    try {
      const questionData = {
        survey_id: id,
        question_text: formData.question_text,
        question_type: formData.question_type,
        options: (formData.question_type === 'radio' || formData.question_type === 'checkbox')
          ? formData.options.filter(opt => opt.trim())
          : null,
        is_required: formData.is_required
      };

      if (editingQuestion) {
        await api.surveys.updateQuestion(editingQuestion.id, questionData);
        toast.success('Question updated successfully!');
      } else {
        await api.surveys.createQuestion(questionData);
        toast.success('Question added successfully!');
      }

      handleCloseModal();
      fetchQuestions();
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error('Failed to save question: ' + (error.response?.data?.message || error.message));
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteClick = (question) => {
    setQuestionToDelete(question);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!questionToDelete) return;
    
    setDeleteLoading(questionToDelete.id);
    
    try {
      await api.surveys.deleteQuestion(questionToDelete.id);
      toast.success('Question deleted successfully!');
      setShowDeleteModal(false);
      setQuestionToDelete(null);
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setQuestionToDelete(null);
  };

  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case 'radio': return FaDotCircle;
      case 'checkbox': return FaCheckSquare;
      case 'textarea': return FaAlignLeft;
      default: return FaDotCircle;
    }
  };

  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case 'radio': return 'Single Choice';
      case 'checkbox': return 'Multiple Choice';
      case 'textarea': return 'Text Response';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

       <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div className="flex items-center gap-4">
                <button
            onClick={() => navigate('/admin/surveys')}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
                >
                  <FaArrowLeft className="w-4 h-4" /> Back
                </button>
              <div>
            <h5 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              {survey?.title || 'Survey Questions'}
            </h5>
          </div>
              </div>
                  <button
          onClick={() => handleOpenModal()}
          className=" border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 py-2 px-8"
        >
          <FaPlus className="w-4 h-4" />
          Add Question
        </button>
            </div>

      {/* Stats */}
      <div className="bg-white rounded-xl shadow-md p-5 mb-6 text-center">
        <p className="text-xs font-medium text-purple-600 uppercase tracking-wider">Total Questions</p>
        <p className="text-4xl font-bold text-purple-700 mt-2">{questions.length}</p>
      </div>

      {/* No Questions */}
      {questions.length === 0 ? (
        <div className="text-center py-16 bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-purple-100">
          <div className="text-6xl text-purple-300 mb-4">Question Mark</div>
          <p className="text-purple-700 font-medium text-lg">
            No questions added yet.
          </p>
          <p className="text-gray-600 text-sm mt-2">
            Click "Add Question" to start building your survey.
          </p>
        </div>
      ) : (
        /* Questions Table */
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-2xl shadow-lg bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-3 px-3 text-left font-bold">#</th>
                <th className="py-3 px-3 text-left font-bold">Question</th>
                <th className="py-3 px-3 text-left font-bold">Type</th>
                <th className="py-3 px-3 text-left font-bold">Options</th>
                <th className="py-3 px-3 text-left font-bold">Required</th>
                <th className="py-3 px-3 text-left font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((question, index) => {
                const TypeIcon = getQuestionTypeIcon(question.question_type);
                const hasOptions = Array.isArray(question.options) && question.options.length > 0;

                return (
                  <tr key={question.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-700 font-bold text-sm">
                        {index + 1}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-800">
                      {question.question_text}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <TypeIcon className="w-5 h-5 text-purple-600" />
                        <span className="font-medium text-gray-700">{getQuestionTypeLabel(question.question_type)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {hasOptions ? (
                        <div className="space-y-1 max-w-xs">
                          {question.options.slice(0, 3).map((option, idx) => (
                            <div key={idx} className="text-sm text-gray-600 truncate">
                              • {option || '(empty)'}
                            </div>
                          ))}
                          {question.options.length > 3 && (
                            <span className="text-xs text-purple-600 font-medium">
                              +{question.options.length - 3} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-left">
                      {question.is_required ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <FaCheck className="w-3 h-3" /> Required
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Optional
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="d-flex gap-1 justify-content-center">
                        <Button
                          bsPrefix="custom-hamburger"
                          onClick={() => handleOpenModal(question)}
                          title="Edit Question"
                          className="border-purple-300 text-purple-600 hover:bg-purple-100 rounded p-1"
                        >
                          <FaEdit className="w-5 h-5" />
                        </Button>
                        <Button
                          bsPrefix="custom-hamburger"
                          onClick={() => handleDeleteClick(question)}
                          title="Delete Question"
                          disabled={deleteLoading === question.id}
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                        >
                          {deleteLoading === question.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FaTrash className="w-5 h-5" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Question Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {editingQuestion ? 'Edit Question' : 'Add New Question'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Question Text <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="question_text"
                    value={formData.question_text}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.question_text}
                    placeholder="Enter your question here..."
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm resize-none"
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.question_text}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3 g-2">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Question Type <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="question_type"
                    value={formData.question_type}
                    onChange={handleInputChange}
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  >
                    <option value="radio">Single Choice (Radio)</option>
                    <option value="checkbox">Multiple Choice (Checkbox)</option>
                    <option value="textarea">Text Response</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {(formData.question_type === 'radio' || formData.question_type === 'checkbox') && (
              <Row className="mb-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Options <span className="text-danger">*</span></Form.Label>
                    <div className="space-y-3">
                      {formData.options.map((option, index) => (
                        <div key={index} className="flex gap-3">
                          <Form.Control
                            type="text"
                            value={option}
                            onChange={(e) => handleOptionChange(index, e.target.value)}
                            placeholder={`Option ${index + 1}`}
                            className="flex-1 rounded-lg border-purple-200 focus:ring-purple-500 text-sm h-11"
                          />
                          {formData.options.length > 2 && (
                            <Button variant="light" onClick={() => removeOption(index)} className="p-2 border-0 bg-red-50 text-red-600 hover:bg-red-100">
                              <FaTrash className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    {formErrors.options && <div className="text-red-500 text-xs mt-2">{formErrors.options}</div>}
                    <div className="mt-3">
                      <Button variant="link" onClick={addOption} className="text-purple-600 hover:text-purple-700 p-0">
                        <FaPlus className="me-2" /> Add Another Option
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group className="d-flex align-items-center gap-3">
                  <Form.Check type="checkbox" id="is_required" name="is_required" checked={formData.is_required} onChange={handleInputChange} />
                  <Form.Label htmlFor="is_required" className="mb-0 text-sm font-medium text-gray-700 cursor-pointer">This question is required</Form.Label>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCloseModal}
                disabled={modalLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleSubmit}
                disabled={modalLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingQuestion ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingQuestion ? 'Update Question' : 'Add Question'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Question</h5>
          <p className="text-gray-600">
            Are you sure you want to delete this question?
          </p>
          {questionToDelete?.question_text && (
            <div className="mt-4 p-3 bg-gray-100 rounded-lg text-left">
              <p className="text-sm text-gray-700 line-clamp-3">
                "{questionToDelete.question_text.substring(0, 100)}{questionToDelete.question_text.length > 100 ? '...' : ''}"
              </p>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-3">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                disabled={!!deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Question'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SurveyQuestions;